import React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import useApiService from 'services/api'
import { DealReport } from 'models/deals_reports'
import { SalesStage } from 'models/sales_stages'
import { User } from 'models/user'
import { PaginatedResponse } from 'models/paginated-response'
import { PiSquaresFour } from 'react-icons/pi'
import { DealCard } from 'components/NewCompanyPage/DealCard'
import { EditDealModal } from 'components/Deals/EditDealModal/EditDealModal'
import {
  getStageColor,
  isTerminalStage,
} from 'components/SalesStage/stageColors'
import { DealsFiltersetV2 } from 'components/Tables/DealsTable/DealsFiltersetV2'
import { OwnerQuickFilter } from 'components/Deals/OwnerQuickFilter'
import {
  boardFilterBadgeClass,
  boardFilterTriggerClass,
} from 'components/Deals/boardToolbarStyles'
import { QuickFilterSearch } from 'components/Deals/QuickFilterSearch'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import { TableSearch } from 'components/Filters/TableSearch'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { FilterIdentifier } from 'models/saved_view'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { getPreferencesQueryKey } from 'services/preferences'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import {
  ds2026DropdownContentClass,
  ds2026DropdownItemClass,
  ds2026DropdownSeparatorClass,
} from 'components/FbUI/ds2026DropdownMenu'
import FbButton from 'components/FbUI/FbButton'
import Checkbox from 'components/Checkbox/checkbox'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import useCachedState from 'utils/useCachedState'
import { cn } from 'components/UI/cn'

// Prefix shared by every paged `report-deals` query (the columns + the toolbar
// counts). Used to reconcile the board cache after an edit — see the note on
// EditDealModal's wiring at the bottom of the board.
const REPORT_DEALS_QUERY_KEY = ['report-deals']

// Sentinel key handed to EditDealModal so its built-in post-save refresh
// (invalidate/refetch of `dealsTableQueryKey`) matches *no* live query and is a
// no-op on the board. The modal can't do a CDC-safe refresh — refetching
// `report-deals` the instant the PATCH resolves races the async CDC read-model
// and snaps stale values back (the exact problem the drag path avoids). We
// neutralize the modal's refresh here and handle it ourselves on close instead.
const EDIT_MODAL_NOOP_QUERY_KEY = ['deals-kanban-edit-noop']

// Deals-filter-store keys that scope by sales stage: the current V1 name plus
// the legacy V2 alias still attached to old saved views. Both resolve to the
// same backend field (`filter_sales_stage_id`) that the board's columns are
// already keyed on — so a stage filter carried over from the table can't
// *filter* the board without emptying every non-matching column. The board
// instead reinterprets it as column visibility (see the wiring in
// DealsKanbanBoard): strip these keys from what the columns/counts query, fold
// the selection into Layout, and suppress the now-redundant chip.
const STAGE_FILTER_KEYS = ['sales_stage', 'sales_stage_id'] as const

// Board "Layout" = which stage columns are shown. It's a view preference, NOT a
// filter: it never touches the deals table or the shared filter store.
//
// Two tiers:
//  - Saved DEFAULT — server-side, per-user (UserBoardLayout, exposed on the
//    preferences payload as `board_layout.hidden_stage_ids`). Follows the rep
//    across devices. "Save as default" writes it; "Reset" reverts to it.
//  - Working OVERRIDE — a per-browser localStorage tweak layered on top of the
//    default (null = "use the default"). Lets a rep adjust their board ad-hoc
//    without changing their saved default; survives reload/navigation.
const HIDDEN_STAGES_STORAGE_KEY = 'deals-kanban-hidden-stage-ids'
const isNumberArray = (v: unknown): v is number[] =>
  Array.isArray(v) && v.every((n) => typeof n === 'number')
const isNullableNumberArray = (v: unknown): v is number[] | null =>
  v === null || isNumberArray(v)

// Each column lazily loads its own stage in pages of this size, with a
// "Load more" button to pull the next page. Mirrors how kanban tools handle
// pipelines of thousands of deals — no column ever has to render everything,
// and a column you never scroll never fetches past its first page.
const COLUMN_PAGE_SIZE = 20

// Sentinel stage id for the read-only "No stage" column. The report-deals
// endpoint treats sales_stage_id=-1 as "unstaged" (IS NULL).
const NO_STAGE_ID = -1

// Share the deals table's filter store so a rep's filters carry across the
// Table/Board toggle (and back) instead of resetting per view. 'DEALS_TABLE'
// is the identifier DealsTableComponentV2 uses on this same page.
const DEALS_FILTER_IDENTIFIER: FilterIdentifier = 'DEALS_TABLE'

// ─── Optimistic cache plumbing for drag-and-drop ─────────────────────────────
//
// Each column is its own paginated `report-deals` query, so moving a card means
// editing two caches: remove it from the source column, prepend it to the
// target. We can't invalidate/refetch (the write hits `contacts/deals/` while
// the board reads the async CDC `reports/deals/` view — refetching immediately
// races propagation and snaps the card back ~half the time), so the optimistic
// edit is authoritative until the panel remounts.

type ReportPage = PaginatedResponse<DealReport>
interface ReportInfinite {
  pages: ReportPage[]
  pageParams: unknown[]
}

function isInfinite(data: unknown): data is ReportInfinite {
  return !!data && typeof data === 'object' && 'pages' in data
}

interface MoveContext {
  dealId: number
  movedDeal: DealReport
  fromStageId: number
  toStageId: number
  /** The `sales_stage_id` this particular query is scoped to (undefined for
   *  the aggregate count queries that carry no stage filter). */
  queryStageFilter: number | undefined
}

/**
 * Apply a stage move to one cached `report-deals` query. Column queries are
 * infinite (`{ pages }`); the toolbar's count queries are flat
 * (`{ results, count }`). The query's own stage filter decides its role:
 * source columns drop the card, the target column gains it, everything else
 * just keeps the moved deal's stage in sync.
 */
function applyOptimisticMove(old: unknown, ctx: MoveContext): unknown {
  const { dealId, movedDeal, fromStageId, toStageId, queryStageFilter } = ctx
  const isSource = queryStageFilter === fromStageId
  const isTarget = queryStageFilter === toStageId

  if (isInfinite(old)) {
    if (isSource) {
      return {
        ...old,
        pages: old.pages.map((p) => ({
          ...p,
          results: p.results.filter((d) => d.id !== dealId),
          count: Math.max(0, p.count - 1),
        })),
      }
    }
    if (isTarget) {
      // Guard against a double-add if the deal is somehow already present.
      const exists = old.pages.some((p) =>
        p.results.some((d) => d.id === dealId)
      )
      return {
        ...old,
        pages: old.pages.map((p, i) => ({
          ...p,
          count: p.count + 1,
          results:
            i === 0 && !exists
              ? [{ ...movedDeal, sales_stage_id: toStageId }, ...p.results]
              : p.results,
        })),
      }
    }
    return old
  }

  // Flat (aggregate count) query: a move doesn't change the filtered/grand
  // totals, so only keep the deal's stage current; leave `count` alone.
  if (old && typeof old === 'object' && 'results' in old) {
    const flat = old as ReportPage
    return {
      ...flat,
      results: flat.results.map((d) =>
        d.id === dealId ? { ...d, sales_stage_id: toStageId } : d
      ),
    }
  }
  return old
}

interface KanbanColumnProps {
  title: string
  /** Real stage id, or NO_STAGE_ID for the unstaged catch-all column. */
  stageId: number
  /** Shared filters (search + filterset), minus pagination. */
  filterParams: Record<string, unknown>
  salesStages: SalesStage[]
  /** Drives per-stage color resolution (custom order vs. days-to-close). */
  customOrderEnabled: boolean
  users: User[] | undefined
  /** Whether cards can be dropped here (false for the read-only "No stage"). */
  droppable: boolean
  isDragTarget?: boolean
  /** Collapse to nothing when the column has no deals (used by "No stage" so an
   *  empty unstaged bucket doesn't take up board space). */
  hideWhenEmpty?: boolean
  onDragStartCard: (deal: DealReport, fromStageId: number) => void
  /** Clears drag state when a drag ends without a valid drop. */
  onDragEndCard?: () => void
  onDragOverColumn?: (e: React.DragEvent) => void
  onDropColumn?: () => void
  onEditDeal: (deal: DealReport) => void
  /** Stage change from a card's "Change stage" menu. `fromStageId` is this
   *  column's stage (the deal's current stage); routes through the same
   *  optimistic move the board uses for drag-and-drop. */
  onStageChangeCard: (
    deal: DealReport,
    fromStageId: number,
    toStageId: number
  ) => void
}

function KanbanColumn({
  title,
  stageId,
  filterParams,
  salesStages,
  customOrderEnabled,
  users,
  droppable,
  isDragTarget,
  hideWhenEmpty,
  onDragStartCard,
  onDragEndCard,
  onDragOverColumn,
  onDropColumn,
  onEditDeal,
  onStageChangeCard,
}: KanbanColumnProps) {
  const api = useApiService()

  // One infinite query per column, scoped to this stage. `limit` is the param
  // the report-deals pagination actually honors (page_size is ignored), so we
  // send both to be explicit; the infinite hook self-corrects page sizes via
  // results.length regardless.
  const [dealsQuery] = api.useGetReportDealsInfinite({
    ...filterParams,
    sales_stage_id: stageId,
    limit: COLUMN_PAGE_SIZE,
    page_size: COLUMN_PAGE_SIZE,
  })
  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = dealsQuery

  const deals = React.useMemo(
    () => data?.pages.flatMap((p) => p.results) ?? [],
    [data]
  )
  // Stage total (server count), not the number loaded so far.
  const count = data?.pages[0]?.count ?? 0
  const remaining = Math.max(0, count - deals.length)

  // The unstaged bucket only earns its space when it has deals. Stay hidden
  // both while the first page is loading AND once it resolves empty — gating on
  // `count === 0` alone (which is true during the initial fetch, before data
  // arrives) means we never render the column just to yank it away when it
  // turns out empty. It pops in only if real deals come back. `!isError` still
  // lets an errored bucket surface its failure state.
  if (hideWhenEmpty && !isError && count === 0) return null

  // Stage identity moved from the deal cards up to the column header: the
  // header takes the stage's colored tone (the same band the card footer used
  // to carry) so the whole column is color-keyed to its stage. The unstaged
  // "No stage" column has no stage → a muted neutral header.
  const stage =
    stageId === NO_STAGE_ID
      ? null
      : (salesStages.find((s) => s.id === stageId) ?? null)
  const tone = stage
    ? getStageColor(stage, salesStages, customOrderEnabled)
    : null

  return (
    <div
      onDragOver={onDragOverColumn}
      onDrop={onDropColumn}
      className={cn(
        'flex w-96 flex-shrink-0 flex-col rounded-xl bg-neutral-100 p-3 transition-colors',
        isDragTarget && 'ring-2 ring-brand-bright ring-offset-1'
      )}
    >
      {/* Column header — colored stage banner + live count pill. The STICKY
          element is the gray wrapper, not the pill: it carries the column's
          neutral bg full-bleed (-mx-3 cancels the column padding) so it masks
          cards scrolling underneath, and its pt-3 holds a constant gap above
          the pill that survives scrolling (-mt-3 cancels the column's own top
          padding so that gap is the same 12px at rest and pinned). The pill
          inside is a fully rounded chip — inset from the edges so its corners
          stay rounded at EVERY scroll position (a full-bleed pill can't, since
          the column body behind it goes straight once its rounded top scrolls
          off). The tone mirrors the old per-card footer band. */}
      <div className="sticky top-0 z-10 -mx-3 -mt-3 bg-neutral-100 p-2.5">
        <div
          className={cn(
            // border-opacity-60 softens the stage-hued stroke (palette strokes
            // are -400, a touch bold on the -50 header fill) without changing
            // the shared StageTone palette other surfaces depend on.
            'flex items-center justify-between gap-2 rounded-t-lg border border-opacity-60 px-4 py-2',
            tone
              ? cn(tone.bg, tone.text, tone.stroke)
              : 'border-neutral-300 bg-neutral-200 text-neutral-700'
          )}
        >
          <h2 className="min-w-0 truncate text-sm font-semibold">{title}</h2>
          <span className="ml-2 flex-shrink-0 rounded-full bg-surface px-2 py-0.5 text-xs font-medium tabular-nums text-fg-secondary">
            {count}
          </span>
        </div>
      </div>

      {/* Cards */}
      <div className="flex flex-col gap-3">
        {deals.map((deal) => (
          <div
            key={deal.id}
            draggable
            onDragStart={() => onDragStartCard(deal, stageId)}
            onDragEnd={onDragEndCard}
            className="cursor-grab active:cursor-grabbing"
          >
            <DealCard
              deal={deal}
              salesStages={salesStages}
              users={users}
              onEdit={onEditDeal}
              onStageChange={(d, toStageId) =>
                onStageChangeCard(d, stageId, toStageId)
              }
              showCompanyName
              // Stage is identified by the colored column header on the board,
              // so the per-card stage banner is suppressed here to give cards
              // back their vertical space.
              showStageFooter={false}
            />
          </div>
        ))}

        {isLoading && (
          <div className="py-8 text-center text-xs text-fg-secondary">
            Loading…
          </div>
        )}

        {isError && (
          <div className="rounded-xl border border-dashed border-error-strong py-8 text-center text-xs text-error-strong">
            Couldn’t load deals.
          </div>
        )}

        {!isLoading && !isError && deals.length === 0 && droppable && (
          <div className="rounded-xl border-2 border-dashed border-ds2026-border-tertiary bg-surface/50 py-8 text-center text-sm font-normal text-neutral-400">
            Drop deals here
          </div>
        )}

        {hasNextPage && (
          <button
            type="button"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="mt-1 rounded-lg border border-line bg-surface px-3 py-2 text-xs font-medium tabular-nums text-fg-secondary transition-colors hover:bg-surface-hover hover:text-fg disabled:opacity-60"
          >
            {isFetchingNextPage
              ? 'Loading…'
              : `Load ${Math.min(COLUMN_PAGE_SIZE, remaining)} more (${remaining} left)`}
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Kanban board for the all-deals page. Columns are pipeline stages; each card
 * is the production DealCard (with company name shown). Every column lazily
 * loads its own stage (paged via "Load more") so the board scales to thousands
 * of deals without rendering them all at once. Dragging a card onto a column
 * writes the new stage via `editDeal`, with an optimistic cross-column cache
 * update so the move feels instant.
 */
export function DealsKanbanBoard() {
  const api = useApiService()
  const queryClient = useQueryClient()

  // Same filter store as the deals table → the funnel button below opens the
  // identical filterset, and applied filters flow straight into every column's
  // report-deals fetch (and persist when toggling back to the table).
  const filterStore = filterStoreRepo.getStore(DEALS_FILTER_IDENTIFIER)
  const filterParams = useFilterParams(filterStore)
  const clearValueFilter = filterStore((s) => s.clearValueFilter)

  // A stage filter carried over from the table (the funnel can't set one on the
  // board — it's hidden there) is reinterpreted as column visibility rather
  // than a data filter. `stageFilterIds` is the set of stage ids the carried
  // filter allows, or null when none is applied. We read both the V1 key and
  // the legacy alias since old saved views can carry either.
  const stageFilterIds = React.useMemo(() => {
    const ids = new Set<number>()
    for (const key of STAGE_FILTER_KEYS) {
      const raw = (filterParams as Record<string, unknown>)[key]
      if (Array.isArray(raw)) {
        for (const v of raw) {
          const n = Number(v)
          if (!Number.isNaN(n)) ids.add(n)
        }
      }
    }
    return ids.size > 0 ? ids : null
  }, [filterParams])

  // filterParams minus the stage keys — what the columns and the count fetch
  // actually query. Stripping the stage filter here is what stops it from
  // emptying non-matching columns; the selection resurfaces as visibility
  // below. The value stays untouched in the shared store, so switching back to
  // the table keeps the rep's stage filter intact.
  const boardFilterParams = React.useMemo(() => {
    if (!stageFilterIds) return filterParams
    const next = { ...filterParams } as Record<string, unknown>
    for (const key of STAGE_FILTER_KEYS) delete next[key]
    return next
  }, [filterParams, stageFilterIds])

  const clearStageFilter = () => {
    for (const key of STAGE_FILTER_KEYS) clearValueFilter(key)
  }

  // Tiny count-only fetches for the toolbar summary: filtered total vs the
  // grand total. Uses boardFilterParams so the stage selection (now a view
  // concern) doesn't narrow the count. React Query dedupes the identical
  // grand-total query the page header also runs.
  const { data: filteredResp } = api.useGetReportDeals({
    ...boardFilterParams,
    page_size: 1,
  })
  const { data: totalResp } = api.useGetReportDeals({ page_size: 1 })
  const { data: stages } = api.useGetSalesStages()
  const { data: users } = api.useGetUsers()
  const { preferences, isSettingEnabled } = usePreferences()
  const customOrderEnabled = isSettingEnabled('sales_stage_custom_order')

  const [dragOverStageId, setDragOverStageId] = React.useState<number | null>(
    null
  )
  // The card being dragged + the stage it started in, captured on drag start so
  // the drop handler can no-op same-column drops and the optimistic update
  // knows which column to remove it from.
  const [dragging, setDragging] = React.useState<{
    deal: DealReport
    fromStageId: number
  } | null>(null)
  const [dealIdToEdit, setDealIdToEdit] = React.useState<number>()

  // Saved default layout — server-side, per-user (cross-device). Comes free on
  // the preferences payload.
  const defaultHiddenStageIds = React.useMemo(
    () => preferences?.board_layout?.hidden_stage_ids ?? [],
    [preferences?.board_layout?.hidden_stage_ids]
  )
  // Working override — a per-browser ad-hoc tweak layered on the default. `null`
  // means "use the saved default" (so a fresh device shows the default), which
  // is also what "Reset" restores.
  const [workingOverride, setWorkingOverride] = useCachedState<number[] | null>(
    null,
    HIDDEN_STAGES_STORAGE_KEY,
    isNullableNumberArray
  )
  const hiddenStageIds = workingOverride ?? defaultHiddenStageIds
  // Setter shim so existing call sites (value or updater) keep working; writes
  // land on the override, seeded from the current effective layout.
  const setHiddenStageIds = (
    next: number[] | ((prev: number[]) => number[])
  ): void => {
    setWorkingOverride((prevOverride) => {
      const prev = prevOverride ?? defaultHiddenStageIds
      return typeof next === 'function' ? next(prev) : next
    })
  }
  // Search narrows the rows shown in the Layout menu; cleared on close.
  const [stageSearch, setStageSearch] = React.useState('')

  const toggleStageVisibility = (stageId: number) => {
    setHiddenStageIds((prev) =>
      prev.includes(stageId)
        ? prev.filter((id) => id !== stageId)
        : [...prev, stageId]
    )
  }

  // Persist the current layout as the user's server-side default. Optimistically
  // update the preferences cache so the trigger reads "Default" immediately, then
  // clear the override so the (now-matching) default shows through.
  const saveLayoutMutation = useMutation({
    mutationFn: (ids: number[]) => api.changeBoardLayout(ids),
    onSuccess: (response, ids) => {
      const saved: number[] = response?.data?.hidden_stage_ids ?? ids
      queryClient.setQueriesData(
        { queryKey: getPreferencesQueryKey() },
        (old: unknown) =>
          old && typeof old === 'object'
            ? { ...old, board_layout: { hidden_stage_ids: saved } }
            : old
      )
      void queryClient.invalidateQueries({ queryKey: getPreferencesQueryKey() })
      setWorkingOverride(null)
      toast.success('Default layout saved')
    },
    onError: () => toast.error('Failed to save default layout'),
  })

  const moveMutation = useMutation({
    mutationFn: ({
      deal,
      toStageId,
    }: {
      deal: DealReport
      fromStageId: number
      toStageId: number
    }) => api.editDeal(deal.id, { sales_stage: toStageId }),
    onMutate: async ({ deal, fromStageId, toStageId }) => {
      await queryClient.cancelQueries({ queryKey: ['report-deals'] })
      const previous: { queryKey: readonly unknown[]; data: unknown }[] = []
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ['report-deals'] })
        .forEach((query) => {
          previous.push({ queryKey: query.queryKey, data: query.state.data })
          // queryKey[1] is JSON.stringify(params); read the stage this query
          // is scoped to so applyOptimisticMove knows its role.
          let queryStageFilter: number | undefined
          try {
            const params = JSON.parse(String(query.queryKey[1] ?? '{}'))
            if (typeof params.sales_stage_id === 'number') {
              queryStageFilter = params.sales_stage_id
            }
          } catch {
            queryStageFilter = undefined
          }
          queryClient.setQueryData(query.queryKey, (old: unknown) =>
            applyOptimisticMove(old, {
              dealId: deal.id,
              movedDeal: deal,
              fromStageId,
              toStageId,
              queryStageFilter,
            })
          )
        })
      return { previous }
    },
    onError: (_err, _vars, context) => {
      context?.previous?.forEach(({ queryKey, data }) => {
        queryClient.setQueryData(queryKey, data)
      })
      toast.error('Failed to move deal. Please try again.')
    },
    onSuccess: (_data, { toStageId }) => {
      const stageName = stages?.find((s) => s.id === toStageId)?.stage
      toast.success(`Deal moved to ${stageName ?? 'new stage'}.`)
    },
    // Deliberately NOT invalidating ['report-deals'] here — see the note on the
    // optimistic plumbing above. Refetching the instant the PATCH resolves
    // races the async CDC reporting view and snaps the card back ~half the
    // time. The optimistic edit is authoritative; onError rolls it back on a
    // real failure, and columns refetch fresh on remount (the Table/Board
    // toggle unmounts the inactive panel) once CDC has caught up.
  })

  // Columns follow the exact order stages are arranged in deal-stage settings:
  // pipeline stages first in their saved `order`, with terminal stages (Closed
  // Won / Closed Lost / Churn) locked to the end. Mirrors SalesStageTable and
  // the deal-detail stage bar so the board doesn't drift from the rest of the
  // app. (Terminal stages carry low/zero `order`, so an `order`-only sort would
  // float them to the front — hence the explicit terminal-last tiebreak.)
  const sortedStages = React.useMemo(
    () =>
      stages
        ? [...stages].sort((a, b) => {
            const aTerminal = isTerminalStage(a)
            const bTerminal = isTerminalStage(b)
            if (aTerminal !== bTerminal) return aTerminal ? 1 : -1
            return a.order - b.order
          })
        : [],
    [stages]
  )

  // Fold a carried stage filter into column visibility: every stage the filter
  // excludes is hidden on top of the rep's manual Layout choices, so
  // "filter to {Negotiation, Closed-Won}" in the table reads as "show only
  // those two columns" on the board.
  const effectiveHiddenStageIds = React.useMemo(() => {
    if (!stageFilterIds) return hiddenStageIds
    const hidden = new Set(hiddenStageIds)
    for (const s of sortedStages) {
      if (!stageFilterIds.has(s.id)) hidden.add(s.id)
    }
    return Array.from(hidden)
  }, [hiddenStageIds, stageFilterIds, sortedStages])

  const visibleStages = React.useMemo(
    () => sortedStages.filter((s) => !effectiveHiddenStageIds.includes(s.id)),
    [sortedStages, effectiveHiddenStageIds]
  )

  const handleDrop = (toStageId: number) => {
    setDragOverStageId(null)
    const current = dragging
    setDragging(null)
    if (!current) return
    // No-op if the card was dropped back onto its current column.
    if (current.fromStageId === toStageId) return
    moveMutation.mutate({
      deal: current.deal,
      fromStageId: current.fromStageId,
      toStageId,
    })
  }

  // Stage change triggered from a card's "Change stage" menu. Routes through
  // the exact same optimistic move as drag-and-drop so it reconciles the
  // per-column caches without the CDC-racing refetch the card's own mutation
  // would otherwise do.
  // Clear drag state when a drag ends without a valid drop (released outside a
  // column, on the toolbar/scrollbar, or via ESC). `handleDrop` only fires on a
  // successful drop, so without this the last-hovered column keeps its
  // ring highlight until the next drag starts.
  const handleDragEnd = () => {
    setDragOverStageId(null)
    setDragging(null)
  }

  const handleMenuStageChange = (
    deal: DealReport,
    fromStageId: number,
    toStageId: number
  ) => {
    if (fromStageId === toStageId) return
    moveMutation.mutate({ deal, fromStageId, toStageId })
  }

  const handleEditModalClose = () => {
    setDealIdToEdit(undefined)
    // Reconcile the board after an edit/delete WITHOUT an immediate refetch:
    // refetching report-deals the instant the PATCH resolves races the async
    // CDC read-model (same reason the drag path skips invalidation — it would
    // snap stale values back). Mark the board's queries stale so they refetch
    // on next mount/focus, by which point CDC has caught up.
    queryClient.invalidateQueries({
      queryKey: REPORT_DEALS_QUERY_KEY,
      refetchType: 'none',
    })
  }

  if (!stages) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-fg-secondary">
        Loading deals…
      </div>
    )
  }

  // Summary reflects the filtered set against the grand total; a filter is
  // "narrowing" when it cuts the matching set below the grand total.
  const matching = filteredResp?.count
  const grandTotal = totalResp?.count
  const filtered =
    matching != null && grandTotal != null && matching < grandTotal

  // Count against the effective hidden set so the trigger badge stays honest
  // when a carried stage filter is hiding columns the rep didn't manually hide.
  const hiddenCount = effectiveHiddenStageIds.filter((id) =>
    sortedStages.some((s) => s.id === id)
  ).length

  // Tri-state "Select all" header. Bulk actions operate on the rep's MANUAL
  // hides (hiddenStageIds), not the effective set — a carried stage filter
  // governs its own excluded columns via the disabled checkboxes + reset item,
  // so bulk toggling shouldn't fight it. "No stage" is part of the set so it
  // bulk-toggles alongside the pipeline columns.
  const allToggleableIds = [NO_STAGE_ID, ...sortedStages.map((s) => s.id)]
  const visibleToggleableCount = allToggleableIds.filter(
    (id) => !hiddenStageIds.includes(id)
  ).length
  const allStagesVisible = visibleToggleableCount === allToggleableIds.length
  const anyStagesVisible = visibleToggleableCount > 0

  // The working layout differs from the saved default → "Save as default" and
  // "Reset" are enabled. Order-independent set comparison so a toggle-off-then-on
  // (same membership, different array order) reads as clean.
  const layoutDirty =
    defaultHiddenStageIds.length !== hiddenStageIds.length ||
    defaultHiddenStageIds.some((id) => !hiddenStageIds.includes(id))
  // Reset = drop the per-browser override and fall back to the saved default.
  const resetToDefaultLayout = () => setWorkingOverride(null)
  const saveDefaultLayout = () => saveLayoutMutation.mutate(hiddenStageIds)

  // Layout search: narrows the menu rows (the "No stage" catch-all and
  // each stage) by name. Empty query shows everything.
  const stageQuery = stageSearch.trim().toLowerCase()
  const shownStages = stageQuery
    ? sortedStages.filter((s) => s.stage.toLowerCase().includes(stageQuery))
    : sortedStages
  const noStageMatches = !stageQuery || 'no stage'.includes(stageQuery)

  return (
    // ds-entity-scroll gives every scroll region inside the board slim,
    // light-gray (Gray/200) scrollbars instead of the heavier browser default.
    <div className="ds-entity-scroll flex min-h-0 flex-1 flex-col">
      {/* Board toolbar — search, filters, and stage visibility in one row,
          closed off by a single divider below so it reads as a distinct
          toolbar. `TableLinkStyleProvider variant="plain"` gives the Filters
          button the same DS2026 chrome it has above the deals table; the
          Layout button mirrors that styling. Search flexes to fill
          the row. */}
      <TableLinkStyleProvider variant="plain">
        <div className="flex items-center gap-3 px-1 py-2">
          <TableSearch
            filterStore={filterStore}
            searchPlaceholder="Search by company, deal name, or ID…"
            className="flex-1"
          />

          {/* Toolbar order: Owner quick filter, then the full "More Filters"
              set, then Layout. Owner is also a quick filter so the most-used
              scope is one click away. */}
          <OwnerQuickFilter filterStore={filterStore} />

          {/* "More Filters" shows the full filterset — including Sales Stage and
              Account Owner — so board users have parity with the table and can
              filter/save a view by either, even though both are also surfaced as
              toolbar quick controls (they share the same store value, so they
              stay in sync). The board strips the stage key from each column's
              query and reinterprets a stage filter as column visibility (see
              stageFilterIds + the "show all stages" reset), so columns never go
              empty. Label reads "More Filters" because the row already shows
              other filters — board-only, not app-wide. */}
          <DealsFiltersetV2
            filterIdentifier={DEALS_FILTER_IDENTIFIER}
            buttonText="More Filters"
          />

          {/* "Layout" (last in the row) is a board view preference (which stage
              columns show), NOT a filter — it never touches the table or the
              filter store. The arrangement saves as a per-browser default; see
              DEFAULT_LAYOUT. */}
          <DropdownMenu onOpenChange={(open) => !open && setStageSearch('')}>
            <DropdownMenuTrigger asChild>
              <FbButton variant="secondary" className={boardFilterTriggerClass}>
                <PiSquaresFour size={16} />
                Layout
                <span className={boardFilterBadgeClass}>
                  {!layoutDirty
                    ? 'Default'
                    : hiddenCount === 0
                      ? 'All'
                      : `${visibleStages.length}/${sortedStages.length}`}
                </span>
              </FbButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className={cn(
                ds2026DropdownContentClass,
                'flex max-h-80 flex-col overflow-hidden'
              )}
            >
              {/* Bulk-select header: a tri-state checkbox that shows/hides all
                  columns, plus "Reset" which reverts the working layout to the
                  saved default. Operates on the working layout — see
                  allToggleableIds above. */}
              <DropdownMenuLabel className="!bg-transparent !px-3 !pb-2 !pt-1.5 flex-shrink-0">
                <div className="flex items-center justify-between gap-2">
                  <div
                    className="flex min-w-0 cursor-pointer items-center gap-2"
                    onClick={() =>
                      setHiddenStageIds(
                        allStagesVisible ? allToggleableIds : []
                      )
                    }
                  >
                    <Checkbox
                      checked={allStagesVisible}
                      indeterminate={anyStagesVisible && !allStagesVisible}
                      readOnly
                      wrapperClassName="flex-shrink-0 pointer-events-none"
                    />
                    <span className="truncate text-sm font-semibold text-neutral-800">
                      All
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={resetToDefaultLayout}
                    disabled={!layoutDirty}
                    className={cn(
                      'flex-shrink-0 text-sm font-medium transition-colors',
                      layoutDirty
                        ? 'text-fg-secondary hover:text-fg'
                        : 'cursor-not-allowed text-neutral-400'
                    )}
                  >
                    Reset
                  </button>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator
                className={cn(ds2026DropdownSeparatorClass, 'flex-shrink-0')}
              />
              <div className="flex-shrink-0 px-1.5 pb-1.5">
                <QuickFilterSearch
                  value={stageSearch}
                  onChange={setStageSearch}
                  placeholder="Search stages"
                />
              </div>
              {/* Scrollable stage list between the fixed header/search and the
                  fixed footer. */}
              <div className="-mx-1.5 min-h-0 flex-1 overflow-y-auto px-1.5">
                {/* When a stage filter rode over from the table we reflect it
                  here (the excluded stages read as unchecked + disabled) and
                  offer a single reset, since individual columns can't be
                  re-shown without lifting the filter. */}
                {stageFilterIds && (
                  <>
                    <DropdownMenuItem
                      onSelect={(e) => e.preventDefault()}
                      onClick={clearStageFilter}
                      className={cn(
                        ds2026DropdownItemClass,
                        '!text-brand-bright'
                      )}
                    >
                      Reflecting a stage filter — show all stages
                    </DropdownMenuItem>
                    <DropdownMenuSeparator
                      className={ds2026DropdownSeparatorClass}
                    />
                  </>
                )}
                {/* The unstaged "No stage" catch-all is toggleable like any
                  pipeline column. Pinned first to mirror its far-left column
                  position. It still self-hides when there are no unstaged
                  deals (hideWhenEmpty on the column), so checking it when none
                  exist simply has nothing to show. */}
                {noStageMatches && (
                  <DropdownMenuItem
                    onSelect={(e) => e.preventDefault()}
                    onClick={() => toggleStageVisibility(NO_STAGE_ID)}
                    className={cn(ds2026DropdownItemClass, '!gap-2.5')}
                  >
                    <Checkbox
                      checked={!effectiveHiddenStageIds.includes(NO_STAGE_ID)}
                      readOnly
                      wrapperClassName="flex-shrink-0 pointer-events-none"
                    />
                    <span className="size-2 flex-shrink-0 rounded-full bg-neutral-300" />
                    No stage
                  </DropdownMenuItem>
                )}
                {noStageMatches && shownStages.length > 0 && (
                  <DropdownMenuSeparator
                    className={ds2026DropdownSeparatorClass}
                  />
                )}
                {shownStages.length === 0 && !noStageMatches && (
                  <p className="px-3 py-2 text-sm text-fg-secondary">
                    No stages found
                  </p>
                )}
                {shownStages.map((stage, idx) => {
                  const isVisible = !effectiveHiddenStageIds.includes(stage.id)
                  // Don't let the last visible column be hidden — an empty board
                  // has nothing to show or drop onto.
                  const isLastVisible = isVisible && visibleStages.length === 1
                  // A carried stage filter owns visibility for the stages it
                  // excludes: those checkboxes are disabled here so the only way
                  // back is the reset above (lifting the filter), not a per-stage
                  // toggle that the filter would immediately override.
                  const isFilterExcluded =
                    stageFilterIds != null && !stageFilterIds.has(stage.id)
                  // Per-stage dot mirrors the "Change stage" menu so the same
                  // color language reads across the board.
                  const dot = getStageColor(
                    stage,
                    stages,
                    customOrderEnabled
                  ).dot
                  // One divider between the pipeline stages and the closed
                  // outcomes (no phase buckets anymore).
                  const prevTerminal =
                    idx > 0 ? isTerminalStage(shownStages[idx - 1]) : false
                  const showDivider =
                    idx > 0 && isTerminalStage(stage) && !prevTerminal
                  const disabled = isLastVisible || isFilterExcluded
                  return (
                    <React.Fragment key={stage.id}>
                      {showDivider && (
                        <DropdownMenuSeparator
                          className={ds2026DropdownSeparatorClass}
                        />
                      )}
                      <DropdownMenuItem
                        disabled={disabled}
                        // Keep the menu open so several columns can be toggled at once.
                        onSelect={(e) => e.preventDefault()}
                        onClick={() =>
                          !disabled && toggleStageVisibility(stage.id)
                        }
                        className={cn(ds2026DropdownItemClass, '!gap-2.5')}
                      >
                        <Checkbox
                          checked={isVisible}
                          disabled={disabled}
                          readOnly
                          wrapperClassName="flex-shrink-0 pointer-events-none"
                        />
                        <span
                          className={cn(
                            'size-2 flex-shrink-0 rounded-full',
                            dot
                          )}
                        />
                        {stage.stage}
                      </DropdownMenuItem>
                    </React.Fragment>
                  )
                })}
              </div>
              {/* Sticky footer: commit the working layout as the saved default
                  (per-browser for now). Enabled only when it differs. */}
              <DropdownMenuSeparator
                className={cn(ds2026DropdownSeparatorClass, 'flex-shrink-0')}
              />
              <div className="flex flex-shrink-0 items-center justify-end px-3 py-1.5">
                <button
                  type="button"
                  onClick={saveDefaultLayout}
                  disabled={!layoutDirty}
                  className={cn(
                    'text-sm font-medium transition-colors',
                    layoutDirty
                      ? 'text-brand-active hover:text-brand-bright'
                      : 'cursor-not-allowed text-neutral-400'
                  )}
                >
                  Save as default
                </button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {/* Active-filter pills live inside the toolbar as their own row so they
            read as part of the filter controls. FilterChips returns null when
            nothing is applied, so this row collapses to zero height when empty.
            Stage keys are excluded because a carried stage filter is surfaced in
            the Layout dropdown; `account_owner` is excluded because it's
            surfaced in the Owner quick filter — both show their own trigger
            badge instead of a redundant chip here. */}
        <FilterChips
          identifier={DEALS_FILTER_IDENTIFIER}
          store={filterStore}
          showActive
          clearAllButton
          excludeKeys={[...STAGE_FILTER_KEYS, 'account_owner']}
          className="!ml-0 mt-0 px-1 pb-2"
        />
      </TableLinkStyleProvider>

      {/* Count summary reflects the filtered set against the grand total
          ("N of TOTAL deals"). */}
      <div className="mb-2 mt-2">
        <p className="px-1 text-xs tabular-nums text-fg-secondary">
          {grandTotal == null
            ? ' '
            : filtered
              ? `Showing ${matching!.toLocaleString()} of ${grandTotal.toLocaleString()} deals`
              : `${grandTotal.toLocaleString()} deals`}
        </p>
      </div>

      <div className="flex-1 overflow-auto">
        {/* px-1/pt-1 give the drag-target column's focus ring (ring-2 +
            ring-offset-1) room so it isn't clipped against the scroll edge.
            items-stretch makes every column as tall as the tallest one, so each
            column's sticky header stays pinned for the full vertical scroll
            (with items-start a short column's header un-sticks the moment you
            scroll past its own content). */}
        <div className="flex items-stretch gap-4 px-1 pb-2 pt-1">
          {/* Unstaged deals, pinned far left. Read-only (no drop target),
              hides itself when empty, and can be toggled off entirely via the
              Layout dropdown. */}
          {!effectiveHiddenStageIds.includes(NO_STAGE_ID) && (
            <KanbanColumn
              title="No stage"
              stageId={NO_STAGE_ID}
              filterParams={boardFilterParams}
              salesStages={sortedStages}
              customOrderEnabled={customOrderEnabled}
              users={users}
              droppable={false}
              hideWhenEmpty
              onDragStartCard={(deal, fromStageId) =>
                setDragging({ deal, fromStageId })
              }
              onDragEndCard={handleDragEnd}
              onEditDeal={(deal) => setDealIdToEdit(deal.id)}
              onStageChangeCard={handleMenuStageChange}
            />
          )}
          {visibleStages.map((stage) => (
            <KanbanColumn
              key={stage.id}
              title={stage.stage}
              stageId={stage.id}
              filterParams={boardFilterParams}
              salesStages={sortedStages}
              customOrderEnabled={customOrderEnabled}
              users={users}
              droppable
              isDragTarget={dragOverStageId === stage.id}
              onDragStartCard={(deal, fromStageId) =>
                setDragging({ deal, fromStageId })
              }
              onDragEndCard={handleDragEnd}
              onDragOverColumn={(e) => {
                e.preventDefault()
                setDragOverStageId(stage.id)
              }}
              onDropColumn={() => handleDrop(stage.id)}
              onEditDeal={(deal) => setDealIdToEdit(deal.id)}
              onStageChangeCard={handleMenuStageChange}
            />
          ))}
        </div>
      </div>

      <EditDealModal
        show={dealIdToEdit != null}
        dealId={dealIdToEdit}
        onClose={handleEditModalClose}
        // Hand the modal a board-private sentinel key that matches no live
        // query, so its built-in post-save refresh is a no-op. We can't let it
        // refetch report-deals on save (races CDC); handleEditModalClose does a
        // CDC-safe stale-mark instead. See the constant's note above.
        dealsTableQueryKey={EDIT_MODAL_NOOP_QUERY_KEY}
        useInvalidateOnSave
      />
    </div>
  )
}
